import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    CircularProgress,
    Paper,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { requestLiveAccessEmail } from '../api/liveAccess';
import { analytics, AnalyticsEvent } from '../services/analytics';
import { glassDialogPaperSx, textFieldDarkSx } from './dialogDarkGlass';

export interface LiveRankingsGateProps {
    /** Shown under the form when magic link failed or was missing */
    accessError?: 'invalid' | 'missing' | null;
    /** Public path to return after email verify (defaults to current pathname). */
    returnPath?: string;
    title?: string;
    subtitle?: string;
}

const alertErrorSx = {
    mb: 2,
    bgcolor: 'rgba(244, 67, 54, 0.15)',
    color: '#ffcdd2',
    border: '1px solid rgba(239, 83, 80, 0.45)',
    '& .MuiAlert-icon': { color: '#ef9a9a' },
} as const;

const alertSuccessSx = {
    mb: 0,
    bgcolor: 'rgba(76, 175, 80, 0.18)',
    color: '#c8e6c9',
    border: '1px solid rgba(129, 199, 132, 0.45)',
    '& .MuiAlert-icon': { color: '#a5d6a7' },
} as const;

const primaryActionSx = {
    backgroundColor: 'rgba(255,255,255,0.14)',
    color: 'white',
    border: '1px solid rgba(255,255,255,0.22)',
    textTransform: 'none',
    fontWeight: 600,
    px: 3,
    borderRadius: '10px',
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.22)',
        boxShadow: 'none',
    },
    '&.Mui-disabled': {
        backgroundColor: 'rgba(255,255,255,0.06)',
        color: 'rgba(255,255,255,0.35)',
        borderColor: 'rgba(255,255,255,0.1)',
    },
} as const;

const LiveRankingsGate: React.FC<LiveRankingsGateProps> = ({
    accessError,
    returnPath = window.location.pathname,
    title = 'Livestreams en YouTube',
    subtitle = 'Ingresa tu correo y te enviaremos un enlace para acceder a esta página.',
}) => {
    const [email, setEmail] = useState('');
    const [newsletterOptIn, setNewsletterOptIn] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        const trimmed = email.trim();
        if (!trimmed) {
            setError('Ingresa tu correo');
            return;
        }
        if (!newsletterOptIn) {
            setError(
                'Marca la casilla para autorizar correos de Podscope y poder enviar el enlace.'
            );
            return;
        }
        setLoading(true);
        try {
            const { message } = await requestLiveAccessEmail(trimmed, newsletterOptIn, returnPath);
            const id = trimmed.toLowerCase();
            analytics.identify(id, { email: id, live_rankings_gate_request: true });
            analytics.track(AnalyticsEvent.LIVE_RANKINGS_MAGIC_LINK_REQUESTED);
            setSent(true);
            if (message) setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Algo salió mal');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                px: 2,
                py: 2,
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    ...glassDialogPaperSx,
                    width: '100%',
                    maxWidth: 480,
                    mx: 'auto',
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        pb: 2,
                        backgroundColor: 'transparent',
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            fontFamily: '"DM Serif Display", serif',
                            color: 'white',
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '0.9rem',
                            color: 'rgba(255, 255, 255, 0.65)',
                            mt: 0.5,
                            textAlign: 'left',
                            lineHeight: 1.45,
                        }}
                    >
                        {subtitle}
                    </Typography>
                </DialogTitle>

                {sent ? (
                    <DialogContent
                        sx={{
                            pt: 2,
                            pb: 3,
                            px: 3,
                            backgroundColor: 'transparent',
                        }}
                    >
                        <Alert severity="success" sx={alertSuccessSx}>
                            Revisa tu correo y abre el enlace para entrar. Puedes cerrar esta
                            pestaña.
                        </Alert>
                    </DialogContent>
                ) : (
                    <Box component="form" onSubmit={handleSubmit}>
                        <DialogContent
                            sx={{
                                pt: 2,
                                pb: 2,
                                px: 3,
                                backgroundColor: 'transparent',
                            }}
                        >
                            {accessError === 'invalid' && (
                                <Alert severity="error" sx={alertErrorSx}>
                                    El enlace caducó o no es válido. Solicita uno nuevo.
                                </Alert>
                            )}
                            {accessError === 'missing' && (
                                <Alert severity="error" sx={alertErrorSx}>
                                    Falta el token de acceso. Abre el enlace del correo.
                                </Alert>
                            )}
                            <TextField
                                type="email"
                                label="Correo"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                autoComplete="email"
                                disabled={loading}
                                fullWidth
                                sx={{ ...textFieldDarkSx, mb: 1 }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={newsletterOptIn}
                                        onChange={e => setNewsletterOptIn(e.target.checked)}
                                        disabled={loading}
                                        sx={{
                                            color: 'rgba(255,255,255,0.45)',
                                            '&.Mui-checked': { color: 'rgba(255,255,255,0.85)' },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '0.85rem',
                                            color: 'rgba(255,255,255,0.72)',
                                            lineHeight: 1.35,
                                            textAlign: 'left',
                                        }}
                                    >
                                        Autorizo a Podscope a enviarme correos con novedades y el
                                        newsletter.
                                    </Typography>
                                }
                                sx={{
                                    alignItems: 'center',
                                    mx: 0,
                                    mr: 0,
                                    '& .MuiFormControlLabel-label': { pl: 0.5 },
                                }}
                            />
                            {error && (
                                <Alert severity="error" sx={{ ...alertErrorSx, mt: 2, mb: 0 }}>
                                    {error}
                                </Alert>
                            )}
                        </DialogContent>
                        <DialogActions
                            sx={{
                                px: 3,
                                pb: 3,
                                pt: 2,
                                justifyContent: 'flex-end',
                                backgroundColor: 'transparent',
                            }}
                        >
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading || !newsletterOptIn}
                                endIcon={
                                    loading ? (
                                        <CircularProgress size={18} sx={{ color: 'white' }} />
                                    ) : (
                                        <SendIcon />
                                    )
                                }
                                sx={primaryActionSx}
                            >
                                {loading ? 'Enviando...' : 'Enviar enlace'}
                            </Button>
                        </DialogActions>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default LiveRankingsGate;
